import React, { useMemo, useState } from 'react';
import Button from '../../ui/Button';
import { buildAiCopyBundle, formatAiCopyBundle, stepTrace } from './mediaCreatorAiCopy';

const pretty = (value) => {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const formatSectionBody = (body, sectionId) => {
  if (body == null || body === '') return null;
  if (typeof body === 'string') return body;
  if (sectionId === 'html-response' && typeof body === 'object' && body.document_html) {
    const copy = { ...body };
    const html = copy.document_html;
    if (typeof html === 'string' && html.length > 2000) {
      copy.document_html = `${html.slice(0, 2000)}\n… truncated (${html.length} chars total)`;
    }
    return pretty(copy);
  }
  return pretty(body);
};

function buildTraceSections(trace) {
  if (!trace) return null;
  const sections = [];
  const intentStep = stepTrace(trace, 'intent') || (trace?.step === 'intent' ? trace : null);
  const htmlStep = stepTrace(trace, 'layout_html');
  const refineStep = stepTrace(trace, 'refine_html');

  const push = (id, label, body, defaultOpen = false) => {
    const formatted = formatSectionBody(body, id);
    if (formatted != null && formatted !== '') {
      sections.push({ id, label, body: formatted, defaultOpen });
    }
  };

  if (intentStep) {
    push('intent-system', 'Intent — system prompt', intentStep.system_prompt);
    push('intent-user', 'Intent — user payload', intentStep.user_payload);
    push('intent-schema', 'Intent — JSON schema', intentStep.schema);
    push('intent-response', 'Intent — model JSON response', intentStep.model_response, true);
    push('intent-normalized', 'Intent — normalized (used for layout)', intentStep.normalized_intent, true);
  } else {
    push('system', 'System prompt', trace.system_prompt);
    push('user', 'User payload (brief + preset)', trace.user_payload);
    push('schema', 'JSON schema', trace.schema);
    push('response', 'Model JSON response', trace.model_response, true);
    push('normalized', 'Normalized intent (used for layout)', trace.normalized_intent, true);
  }

  if (htmlStep) {
    push('html-system', 'Layout HTML — system prompt', htmlStep.system_prompt);
    push('html-user', 'Layout HTML — user payload', htmlStep.user_payload);
    push('html-schema', 'Layout HTML — JSON schema', htmlStep.schema);
    push('html-response', 'Layout HTML — model JSON response', htmlStep.model_response, true);
  }

  if (refineStep) {
    push('refine-system', 'Refine — system prompt', refineStep.system_prompt);
    push('refine-user', 'Refine — user payload', refineStep.user_payload);
    push('refine-schema', 'Refine — JSON schema', refineStep.schema);
    push('refine-response', 'Refine — model JSON response', refineStep.model_response, true);
  }

  push('note', 'Notes / errors', trace.note || trace.error);
  if (trace.html_fallback_reason) {
    push('html-fallback', 'HTML fallback reason', trace.html_fallback_reason);
  }
  if (trace.html_fallback_trace) {
    push('html-fallback-trace', 'HTML fallback trace', trace.html_fallback_trace);
  }
  if (trace.html_salvage_error) {
    push('html-salvage', 'HTML salvage error', trace.html_salvage_error);
  }

  return sections.length ? sections : null;
}

const MediaCreatorAiTrace = ({ layout, brief, onRegenerate, regenerating }) => {
  const compiler = layout?.ai?.compiler;
  const [open, setOpen] = useState(() => compiler === 'html_document');
  const [copyState, setCopyState] = useState('idle');
  const trace = layout?.ai?.trace;
  const copyBundle = useMemo(() => buildAiCopyBundle(layout, brief), [layout, brief]);
  const normalizedIntent = copyBundle.normalized_intent;
  const headline = normalizedIntent?.headline;

  const sections = useMemo(() => buildTraceSections(trace), [trace]);

  const copyAll = async () => {
    const text = formatAiCopyBundle(copyBundle);
    try {
      await navigator.clipboard.writeText(text);
      setCopyState('copied');
      window.setTimeout(() => setCopyState('idle'), 2000);
    } catch {
      setCopyState('failed');
      window.setTimeout(() => setCopyState('idle'), 2500);
    }
  };

  if (!layout?.ai) {
    return (
      <div className="mc-ai-trace">
        <p className="dd-muted mc-ai-trace-empty">
          No AI metadata stored on this design (created before trace support or not yet re-run).
        </p>
        {brief && (
          <p className="mc-ai-trace-summary">
            <strong>Brief:</strong> {brief.slice(0, 240)}
            {brief.length > 240 ? '…' : ''}
          </p>
        )}
        <div className="mc-ai-trace-actions">
          <Button size="sm" variant="secondary" onClick={copyAll}>
            {copyState === 'copied' ? 'Copied' : copyState === 'failed' ? 'Copy failed' : 'Copy AI details'}
          </Button>
          {onRegenerate && (
            <Button size="sm" variant="secondary" loading={regenerating} onClick={onRegenerate}>
              Re-run AI layout
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="mc-ai-trace">
      <button type="button" className="mc-ai-trace-toggle" onClick={() => setOpen((v) => !v)}>
        {open ? 'Hide' : 'Show'} AI prompt &amp; JSON response
        <span className="dd-muted">
          {' '}
          — v{layout.ai.prompt_version || '?'}
          {compiler ? ` · ${compiler}` : ''}
          {!open && headline ? ` · ${headline}` : ''}
        </span>
      </button>
      <div className="mc-ai-trace-actions">
        <Button size="sm" variant="secondary" onClick={copyAll}>
          {copyState === 'copied' ? 'Copied' : copyState === 'failed' ? 'Copy failed' : 'Copy all AI details'}
        </Button>
        {onRegenerate && (
          <Button size="sm" variant="ghost" loading={regenerating} onClick={onRegenerate}>
            Re-run AI
          </Button>
        )}
      </div>
      {open && (
        <div className="mc-ai-trace-body">
          {headline && (
            <p className="mc-ai-trace-summary">
              <strong>Headline:</strong> {headline}
              {normalizedIntent?.layout_variant ? ` · ${normalizedIntent.layout_variant}` : ''}
            </p>
          )}
          {sections ? (
            sections.map((sec) => (
              <details
                key={sec.id}
                className="mc-ai-trace-section"
                open={sec.defaultOpen}
              >
                <summary>{sec.label}</summary>
                <pre>{sec.body}</pre>
              </details>
            ))
          ) : (
            <pre>{pretty(layout.ai)}</pre>
          )}
        </div>
      )}
    </div>
  );
};

export default MediaCreatorAiTrace;
